/**
 * Pure helpers for the grouped "cutoff approaching" feed card. The backend emits
 * one `cutoff_approaching` root event per booking inside the alert window, which
 * floods the feed with near-identical rows. These helpers classify those events
 * and bucket them by days-until-cutoff so the row component can render a single
 * summary card with an urgency-ordered drill-down. Kept separate from the
 * component so they can be unit-tested without a DOM.
 */
import type { FeedEvent } from "@/types/feed";

/** Whether an event is a per-booking cutoff-approaching root. */
export function isCutoffApproachingEvent(event: FeedEvent): boolean {
  return event.event_type === "cutoff_approaching";
}

function readNumber(payload: unknown, field: string): number | null {
  if (payload && typeof payload === "object") {
    const value = (payload as Record<string, unknown>)[field];
    if (typeof value === "number" && Number.isFinite(value)) return value;
  }
  return null;
}

function readString(payload: unknown, field: string): string | null {
  if (payload && typeof payload === "object") {
    const value = (payload as Record<string, unknown>)[field];
    if (typeof value === "string" && value.trim()) return value;
  }
  return null;
}

/** Days until cutoff from the event payload, or `null` when absent. */
export function cutoffDaysUntil(event: FeedEvent): number | null {
  return readNumber(event.payload, "days_until");
}

/** ISO cutoff date from the event payload, or `null` when absent. */
export function cutoffDate(event: FeedEvent): string | null {
  return readString(event.payload, "cutoff_date");
}

/** Booking id for a cutoff event, preferring the event reference. */
export function cutoffBookingId(event: FeedEvent): string | null {
  return event.reference_id ?? readString(event.payload, "source_id");
}

/** One days-until bucket of cutoff events, ordered by urgency. */
export interface CutoffDayBucket {
  /** Days until cutoff, or `null` when the payload didn't carry it. */
  daysUntil: number | null;
  events: FeedEvent[];
}

/**
 * Bucket cutoff events by their days-until value, ascending (most urgent first).
 * Events missing a days-until value fall into a trailing `null` bucket.
 */
export function groupCutoffEventsByDays(events: FeedEvent[]): CutoffDayBucket[] {
  const buckets = new Map<number, FeedEvent[]>();
  const unknown: FeedEvent[] = [];

  for (const event of events) {
    const days = cutoffDaysUntil(event);
    if (days === null) {
      unknown.push(event);
      continue;
    }
    const list = buckets.get(days);
    if (list) list.push(event);
    else buckets.set(days, [event]);
  }

  const ordered: CutoffDayBucket[] = [...buckets.entries()]
    .sort(([a], [b]) => a - b)
    .map(([daysUntil, bucketEvents]) => ({ daysUntil, events: bucketEvents }));

  if (unknown.length > 0) {
    ordered.push({ daysUntil: null, events: unknown });
  }

  return ordered;
}

/**
 * A feed list item after cutoff collapse: either a normal event or the single
 * card that stands in for every `cutoff_approaching` event in the list.
 */
export type CutoffGroupedItem =
  | { kind: "event"; event: FeedEvent }
  | { kind: "cutoff_group"; events: FeedEvent[] };

/**
 * Collapse every `cutoff_approaching` event into one summary item, positioned at
 * the first (newest, given a newest-first input) cutoff event's slot. All other
 * events keep their order and render individually.
 */
export function collapseCutoffEvents(events: FeedEvent[]): CutoffGroupedItem[] {
  const cutoffEvents = events.filter(isCutoffApproachingEvent);
  const items: CutoffGroupedItem[] = [];
  let inserted = false;
  for (const event of events) {
    if (isCutoffApproachingEvent(event)) {
      if (!inserted) {
        inserted = true;
        items.push({ kind: "cutoff_group", events: cutoffEvents });
      }
      continue;
    }
    items.push({ kind: "event", event });
  }
  return items;
}
